/**
 * printablesRestoreLedger — per-user "do not auto-restore" list for purchased
 * printables, keyed by printableId in localStorage.
 *
 * Why: entitlements are the server source of truth and usePrintablesLibrarySync
 * re-restores any entitled printable missing from the local library. Without
 * this ledger a user-deleted purchase silently reappears on every sign-in /
 * app start (one leg of the 2026-06/07 duplication bug — see printLibrary
 * changelog 2026-07). Deleting a purchase in PrintLibrary adds it here;
 * explicitly re-buying / re-adding it from Printables removes it.
 *
 * Local-only by design: deletion stickiness does not follow the account across
 * machines — on a fresh device entitlements still restore in full, which is the
 * desired "my purchases follow me" behavior.
 */
const key = (userId) => `pl_suppressed_printables_${userId}`;

function read(userId) {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(key(userId));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function write(userId, ids) {
  try { localStorage.setItem(key(userId), JSON.stringify(ids)); } catch { /* non-fatal */ }
}

export function suppressPrintableRestore(userId, printableId) {
  if (!userId || !printableId) return;
  const ids = read(userId);
  if (!ids.includes(printableId)) {
    ids.push(printableId);
    write(userId, ids);
  }
}

export function unsuppressPrintableRestore(userId, printableId) {
  if (!userId || !printableId) return;
  const ids = read(userId);
  const next = ids.filter((x) => x !== printableId);
  if (next.length !== ids.length) write(userId, next);
}

export function isPrintableRestoreSuppressed(userId, printableId) {
  if (!userId || !printableId) return false;
  return read(userId).includes(printableId);
}
